import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import xlsx from 'xlsx';
import MainLayout from '../components/layouts/MainLayout';
import ReportList from '../components/views/reports/ReportList';
import { ResponseFailModal } from '../components/modal/Response';
import Spin from '../components/Spin';
import { reportListAction } from '../stores/actions';
import * as api from '../api';
import { reportDataExcelColumns, reportListFormatter } from '../lib/formatter';
import useDebounce from '../lib/useDebounce';

const ReportListContainer = ({history}) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isOpenApiErrorModal, setIsOpenApiErrorModal ] = useState(false);
  const [ isDebounce, setIsDebounce ] = useState(false); //디바운싱 사용 구분을 위해 추가
  const [ apiErrorMessage, setApiErrorMessage ] = useState('');
  const [ apiErrorSubMessage, setApiErrorSubMessage ] = useState('');
  const { reportListTotal, reportListItems, currentPageNumber, paginationItem, filterItem } = useSelector(state => state.reportList);
  const dispatch = useDispatch();

  const setReportList = useCallback((reportList) => {
    dispatch(reportListAction.setReportList(reportList));
  }, [dispatch]);

  const setCurrentPageNumber = useCallback((currentPageNumber) => {
    dispatch(reportListAction.setCurrentPageNumber(currentPageNumber));
  }, [dispatch]);

  const setPaginationItem = useCallback((paginationItem) => {
    dispatch(reportListAction.setPaginationItem(paginationItem));
  }, [dispatch]);

  const setFilterItem = useCallback((filterItem, isDebounce) => {
    setIsDebounce(isDebounce); //디바운싱 처리를 위해 하위 Component에서 구분을 받아옴
    dispatch(reportListAction.setFilterItem(filterItem));
  }, [dispatch]);

  const listReport = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await api.listReport({...filterItem});
      setReportList(res.data);
    } catch (error) {
      setReportList({
        total: 0,
        items: [],
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [setReportList, filterItem]);

  const exportDataToExcel = () => {
    try {
      setIsLoading(true);
      let excelDataColumns = reportDataExcelColumns(filterItem.reportType);
      const excelDatas = reportListItems.map((report) => {
        const formattedData = {};
        excelDataColumns.map((column) => {
          _.set(formattedData, column.name, reportListFormatter(report, column.key));
          if (!_.isString(formattedData[column.name])) _.set(formattedData, column.name, JSON.stringify(formattedData[column.name]));
          return formattedData;
        });
        return formattedData;
      });
      const worksheet = xlsx.utils.json_to_sheet(excelDatas);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, 'sheet1');
      xlsx.writeFile(workbook, `${filterItem.reportType}_report.xlsx`);
    } catch (error) {
      setApiErrorMessage('리포트 엑셀 다운로드 실패');
      setApiErrorSubMessage('');
      setIsOpenApiErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  //디바운싱 훅으로 이동
  const handleDebounce = useCallback(
    useDebounce((listReport) => listReport(), 300) //0.3초 동안 미입력 시 함수 실행
  ,[]);

  useEffect(() => {
    try {
      !isDebounce? listReport() : handleDebounce(listReport); //디바운싱 처리
    } catch (error) {
      throw error;
    }
  }, [listReport, isDebounce, handleDebounce]);

  return (
    <Fragment>
      <Spin isSpin={isLoading}/>
      <MainLayout
        history={history}
        refresh={() => listReport()}
        ContentBody={(
          <Fragment>
            <ReportList
              reportListTotal={reportListTotal}
              reportListItems={reportListItems}
              filterItem={filterItem}
              paginationItem={paginationItem}
              currentPageNumber={currentPageNumber}
              listReportApi={listReport}
              exportDataToExcel={exportDataToExcel}
              setFilterItem={setFilterItem}
              setPaginationItem={setPaginationItem}
              setCurrentPageNumber={setCurrentPageNumber}
              history={history}
            />
            <ResponseFailModal
              isOpen={isOpenApiErrorModal}
              toggle={() => setIsOpenApiErrorModal(!isOpenApiErrorModal)}
              message={apiErrorMessage}
              subMessage={apiErrorSubMessage}
            />
          </Fragment>
        )}
      />
    </Fragment>
  );
}

export default ReportListContainer;